import React, { useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { addClient } from '../../store/slices/clientsSlice';
import { Client, IndividualClient, RetailerClient } from '../../types/client';
import { v4 as uuidv4 } from 'uuid';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ClientFormData {
  customerType: 'Individual' | 'Retailer';
  customerName: string;
  contactName: string;
  email: string;
  email2: string;
  phone: string;
  phone2: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();

  const getInitialFormData = (): ClientFormData => ({
    customerType: 'Individual',
    customerName: '',
    contactName: '',
    email: '',
    email2: '',
    phone: '',
    phone2: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: ''
  });

  const [formData, setFormData] = useState<ClientFormData>(getInitialFormData());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [showEmail2, setShowEmail2] = useState(false);
  const [showPhone2, setShowPhone2] = useState(false);

  const handleFieldChange = (field: keyof ClientFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user corrects it
    if (attemptedSubmit && validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleAddEmail2 = () => {
    setShowEmail2(true);
  };

  const handleRemoveEmail2 = () => {
    setShowEmail2(false);
    setFormData(prev => ({ ...prev, email2: '' }));
  };

  const handleAddPhone2 = () => {
    setShowPhone2(true);
  };

  const handleRemovePhone2 = () => {
    setShowPhone2(false);
    setFormData(prev => ({ ...prev, phone2: '' }));
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Validate customer name
    if (!formData.customerName.trim()) {
      errors.customerName =
        formData.customerType === 'Retailer' ? 'Retailer name is required' : 'Customer name is required';
    }

    return errors;
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setValidationErrors({});
    setAttemptedSubmit(false);
    setShowEmail2(false);
    setShowPhone2(false);
  };

  const handleSaveClient = () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setAttemptedSubmit(true);
      setValidationErrors(errors);
      return;
    }

    // Build the full address from individual fields
    const addressParts = [
      formData.streetAddress,
      formData.addressLine2,
      formData.city,
      formData.province,
      formData.postalCode
    ].filter(part => part.trim() !== '');

    const fullAddress = addressParts.join(', ');

    // Create client based on type
    let newClient: Client;

    if (formData.customerType === 'Individual') {
      newClient = {
        id: uuidv4(),
        type: 'individual',
        name: formData.customerName,
        email: formData.email,
        phone: formData.phone,
        address: fullAddress,
        notes: '',
        preferences: '',
        createdAt: new Date().toISOString()
      } as IndividualClient;
    } else {
      newClient = {
        id: uuidv4(),
        type: 'retailer',
        retailerName: formData.customerName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        address: fullAddress,
        notes: '',
        preferences: '',
        createdAt: new Date().toISOString()
      } as RetailerClient;
    }

    dispatch(addClient(newClient));
    console.log(`New client created: ${formData.customerName} (${newClient.id})`);

    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create New Client</h2>
          <button style={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <form style={styles.form} onSubmit={e => e.preventDefault()}>
          {/* Customer Type Selection */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Customer Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="customerType"
                  value="Individual"
                  checked={formData.customerType === 'Individual'}
                  onChange={e => handleFieldChange('customerType', e.target.value as 'Individual' | 'Retailer')}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Individual</span>
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="customerType"
                  value="Retailer"
                  checked={formData.customerType === 'Retailer'}
                  onChange={e => handleFieldChange('customerType', e.target.value as 'Individual' | 'Retailer')}
                  style={styles.radioInput}
                />
                <span style={styles.radioText}>Retailer</span>
              </label>
            </div>
          </div>

          {/* Customer Information Section */}
          <h3 style={styles.sectionTitle}>Customer Information</h3>
          <div style={styles.twoColumnGrid}>
            {/* Left Column */}
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {formData.customerType === 'Retailer' ? 'Retailer Name *' : 'Customer Name *'}
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={e => handleFieldChange('customerName', e.target.value)}
                  style={styles.input}
                  placeholder={formData.customerType === 'Retailer' ? 'Enter retailer name' : 'Enter customer name'}
                  autoFocus
                />
                {attemptedSubmit && validationErrors.customerName && (
                  <div style={styles.error}>{validationErrors.customerName}</div>
                )}
              </div>

              {formData.customerType === 'Retailer' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Contact Name</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={e => handleFieldChange('contactName', e.target.value)}
                    style={styles.input}
                    placeholder="Enter contact person name"
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleFieldChange('email', e.target.value)}
                  style={styles.input}
                  placeholder="customer@example.com"
                />
                {!showEmail2 && (
                  <button type="button" onClick={handleAddEmail2} style={styles.addSecondaryButton}>
                    + Add Email
                  </button>
                )}
                {showEmail2 && (
                  <div style={styles.secondaryFieldContainer}>
                    <input
                      type="email"
                      value={formData.email2}
                      onChange={e => handleFieldChange('email2', e.target.value)}
                      style={styles.input}
                      placeholder="secondary@example.com"
                    />
                    <button type="button" onClick={handleRemoveEmail2} style={styles.removeSecondaryButton}>
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleFieldChange('phone', e.target.value)}
                  style={styles.input}
                  placeholder="(555) 123-4567"
                />
                {!showPhone2 && (
                  <button type="button" onClick={handleAddPhone2} style={styles.addSecondaryButton}>
                    + Add Phone
                  </button>
                )}
                {showPhone2 && (
                  <div style={styles.secondaryFieldContainer}>
                    <input
                      type="tel"
                      value={formData.phone2}
                      onChange={e => handleFieldChange('phone2', e.target.value)}
                      style={styles.input}
                      placeholder="(555) 987-6543"
                    />
                    <button type="button" onClick={handleRemovePhone2} style={styles.removeSecondaryButton}>
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Street Address</label>
                <input
                  type="text"
                  value={formData.streetAddress}
                  onChange={e => handleFieldChange('streetAddress', e.target.value)}
                  style={styles.input}
                  placeholder="123 Main St"
                />
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address Line 2</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={e => handleFieldChange('addressLine2', e.target.value)}
                  style={styles.input}
                  placeholder="Apt, Suite, Unit, etc."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => handleFieldChange('city', e.target.value)}
                  style={styles.input}
                  placeholder="Toronto"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Province</label>
                <input
                  type="text"
                  value={formData.province}
                  onChange={e => handleFieldChange('province', e.target.value)}
                  style={styles.input}
                  placeholder="ON"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={e => handleFieldChange('postalCode', e.target.value)}
                  style={styles.input}
                  placeholder="A1A 1A1"
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div style={styles.modalFooter}>
            <button type="button" onClick={handleClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="button" onClick={handleSaveClient} style={styles.saveButton}>
              Save New Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    padding: '20px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginTop: '16px',
    marginBottom: '12px'
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px'
  },
  radioInput: {
    marginRight: '8px',
    cursor: 'pointer'
  },
  radioText: {
    color: '#333'
  },
  addSecondaryButton: {
    marginTop: '8px',
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: '#2196F3',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'left',
    display: 'inline-block'
  },
  secondaryFieldContainer: {
    marginTop: '8px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  removeSecondaryButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    lineHeight: '1',
    minWidth: '36px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default CreateClientModal;
